import { extend } from 'vee-validate'
import * as rules from 'vee-validate/dist/rules'
import DataUtils from '@/utils/DataUtils'
import ObjArrUtils from '@/utils/ObjArrUtils'
import i18n from '@/plugins/i18n'

Object.keys(rules).forEach(rule => {
  extend(rule, {
    ...rules[rule]
  })
})

const veeValidateRules = {
  alpha: {
    description: 'The field under validation may only contain alphabetic characters.'
  },
  alpha_dash: {
    description: 'The field under validation may contain alphabetic characters, numbers, dashes or underscores.'
  },
  alpha_num: {
    description: 'The field under validation may contain alphabetic characters or numbers.'
  },
  alpha_spaces: {
    description: 'The field under validation may contain alphabetic characters or spaces.'
  },
  between: {
    description: 'The field under validation must have a numeric value bounded by a minimum value and a maximum value.'
  },
  confirmed: {
    description: 'The field under validation must have the same value as the confirmation field.'
  },
  digits: {
    description: 'The field under validation must be numeric and have the specified number of digits.'
  },
  dimensions: {
    description: 'The file added to the field under validation must be an image (jpg,svg,jpeg,png,bmp,gif) having the exact specified dimension.'
  },
  double: {
    description: 'The field under validation must have a valid decimal value.'
  },
  email: {
    description: 'The field under validation must be a valid email.'
  },
  excluded: {
    description: 'The field under validation must have a value that is not in the specified list. Uses double equals for checks.'
  },
  ext: {
    description: 'The file added to the field under validation must have one of the extensions specified.'
  },
  image: {
    description: 'The file added to the field under validation must have an image mime type (image/*).'
  },
  integer: {
    description: 'The field under validation must be a valid integer value. Doesn\'t accept exponentiale notation.'
  },
  is: {
    description: 'The field under validation must match the given value, uses strict equality.'
  },
  is_not: {
    description: 'The field under validation must not match the given value, uses strict equality.'
  },
  length: {
    description: 'The field under validation must have exactly have the specified number of items, only works for iterables.'
  },
  max: {
    description: 'The field under validation length may not exceed the specified length.'
  },
  max_value: {
    description: 'The field under validation must be a numeric value and must not be greater than the specified value.'
  },
  mimes: {
    description: 'The file type added to the field under validation should have one of the specified mime types.'
  },
  min: {
    description: 'The field under validation length should not be less than the specified length.'
  },
  min_value: {
    description: 'The field under validation must be a numeric value and must not be less than the specified value.'
  },
  numeric: {
    description: 'The field under validation must only consist of numbers.'
  },
  oneOf: {
    description: 'The field under validation must have a value that is in the specified list. Uses double equals for checks.'
  },
  regex: {
    description: 'The field under validation must match the specified regular expression.'
  },
  required: {
    description: 'The field under validation must have a non-empty value.'
  },
  required_if: {
    description: 'The field under validation must have a non-empty value only if the target field (first argument) is set to one of the specified values (other arguments).'
  },
  size: {
    description: 'The file size added to the field under validation must not exceed the specified size in kilobytes.'
  }
}

// #**---------------------------------------- Enhanced Validation Rules ----------------------------------------**# //

/*
min:value

# The field under validation length should not be less than the specified length.
*/
extend('min', {
  validate (value, args) {
    const result = rules.min.validate(value, args)
    args.min = args.length
    return result
  },
  params: ['length', 'min'],
  message: (_, values) => i18n.t('validations.min', values)
})

/*
max:value

# The field under validation length should not be greater than the specified length.
*/
extend('max', {
  validate (value, args) {
    const result = rules.max.validate(value, args)
    args.max = args.length
    return result
  },
  params: ['length', 'max'],
  message: (_, values) => i18n.t('validations.max', values)
})

/*
digits:value

# The field under validation must be numeric and have the specified number of digits.
*/
extend('digits', {
  validate (value, args) {
    const result = rules.digits.validate(value, args)
    args.digits = args.length
    return result
  },
  params: ['length', 'digits'],
  message: (_, values) => i18n.t('validations.digits', values)
})

/*
required_if:anotherfield,value,...

# The field under validation must be numeric and have the specified number of digits. (Modified to match Laravel's method)
*/
extend('required_if', {
  ...rules.required_if,
  validate (value, args) {
    if (!ObjArrUtils.isEmpty(args.values)) {
      return rules.required_if.validate(value, args)
    }
    throw new Error("Invalid Number Of Arguments: 'values' argument not supplied")
  },
  message: (_, values) => i18n.t('validations.required_if', values)
})

// #**---------------------------------------- Customs Validation Rules ----------------------------------------**# //

/*
required_unless:anotherfield,value,...

# The field under validation must be present and not empty unless the anotherfield field is equal to any value.
*/
extend('required_unless', {
  ...rules.required,
  validate (value, args) {
    if (!ObjArrUtils.isEmpty(args.arr)) {
      return (!ObjArrUtils.isEmpty(value) || args.arr.includes(args.field))
    } else {
      throw new Error("Invalid Number Of Arguments: 'values' argument not supplied")
    }
  },
  params: [{ name: 'field', isTarget: true }, 'arr'],
  message: (_, values) => i18n.t('validations.required_unless', values)
})

/*
required_with:foo,bar,.. (Currently Supports Until 6 Arguments)

# The field under validation must be present and not empty only if any of the other specified fields are present.
*/
extend('required_with', {
  ...rules.required,
  validate (value, args) {
    let required = false

    // eslint-disable-next-line
    for (let key of Object.keys(args)) {
      if (typeof args[key] !== 'function') {
        if (!ObjArrUtils.isEmpty(args[key])) { required = true }
      }
    }
    return (!ObjArrUtils.isEmpty(value) || !required)
  },
  params: [
    { name: 'val1', isTarget: true },
    { name: 'val2', isTarget: true },
    { name: 'val3', isTarget: true },
    { name: 'val4', isTarget: true },
    { name: 'val5', isTarget: true },
    { name: 'val6', isTarget: true }
  ],
  message: (_, values) => i18n.t('validations.required_with', values)
})

/*
required_with_all:foo,bar,.. (Currently Supports Until 6 Arguments)

# The field under validation must be present and not empty only if all of the other specified fields are present.
*/
extend('required_with_all', {
  ...rules.required,
  validate (value, args) {
    let required = true

    // eslint-disable-next-line
    for (let key of Object.keys(args)) {
      if (typeof args[key] !== 'function') {
        if (ObjArrUtils.isEmpty(args[key])) { required = false }
      }
    }
    return (!ObjArrUtils.isEmpty(value) || !required)
  },
  params: [
    { name: 'val1', isTarget: true },
    { name: 'val2', isTarget: true },
    { name: 'val3', isTarget: true },
    { name: 'val4', isTarget: true },
    { name: 'val5', isTarget: true },
    { name: 'val6', isTarget: true }
  ],
  message: (_, values) => i18n.t('validations.required_with_all', values)
})

/*
required_without:foo,bar,.. (Currently Supports Until 6 Arguments)

# The field under validation must be present and not empty only when any of the other specified fields are not present.
*/
extend('required_without', {
  ...rules.required,
  validate (value, args) {
    let pass = false

    // eslint-disable-next-line
    for (let key of Object.keys(args)) {
      if (typeof args[key] !== 'function') {
        if (!ObjArrUtils.isEmpty(args[key])) { pass = true }
      }
    }
    return (!ObjArrUtils.isEmpty(value) || pass)
  },
  params: [
    { name: 'val1', isTarget: true },
    { name: 'val2', isTarget: true },
    { name: 'val3', isTarget: true },
    { name: 'val4', isTarget: true },
    { name: 'val5', isTarget: true },
    { name: 'val6', isTarget: true }
  ],
  message: (_, values) => i18n.t('validations.required_without', values)
})

/*
required_without_all:foo,bar,.. (Currently Supports Until 6 Arguments)

# The field under validation must be present and not empty only when all of the other specified fields are not present.
*/
extend('required_without_all', {
  ...rules.required,
  validate (value, args) {
    let pass = true

    // eslint-disable-next-line
    for (let key of Object.keys(args)) {
      if (typeof args[key] !== 'function') {
        if (ObjArrUtils.isEmpty(args[key])) { pass = false }
      }
    }
    return (!ObjArrUtils.isEmpty(value) || pass)
  },
  params: [
    { name: 'val1', isTarget: true },
    { name: 'val2', isTarget: true },
    { name: 'val3', isTarget: true },
    { name: 'val4', isTarget: true },
    { name: 'val5', isTarget: true },
    { name: 'val6', isTarget: true }
  ],
  message: (_, values) => i18n.t('validations.required_without_all', values)
})

/*
sometimes
*/
extend('sometimes', {
  validate (value, args) {
    return true
  }
})

/*
nullable
*/
extend('nullable', {
  validate (value, args) {
    return true
  }
})

/*
url

# The field under validation must be a valid URL.
*/
extend('url', {
  validate: value => {
    // eslint-disable-next-line
    const regex = /^(aaa|aaas|about|acap|acct|acd|acr|adiumxtra|adt|afp|afs|aim|amss|android|appdata|apt|ark|attachment|aw|barion|beshare|bitcoin|bitcoincash|blob|bolo|browserext|calculator|callto|cap|cast|casts|chrome|chrome-extension|cid|coap|coap\+tcp|coap\+ws|coaps|coaps\+tcp|coaps\+ws|com-eventbrite-attendee|content|conti|crid|cvs|dab|data|dav|diaspora|dict|did|dis|dlna-playcontainer|dlna-playsingle|dns|dntp|dpp|drm|drop|dtn|dvb|ed2k|elsi|example|facetime|fax|feed|feedready|file|filesystem|finger|first-run-pen-experience|fish|fm|ftp|fuchsia-pkg|geo|gg|git|gizmoproject|go|gopher|graph|gtalk|h323|ham|hcap|hcp|http|https|hxxp|hxxps|hydrazone|iax|icap|icon|im|imap|info|iotdisco|ipn|ipp|ipps|irc|irc6|ircs|iris|iris\.beep|iris\.lwz|iris\.xpc|iris\.xpcs|isostore|itms|jabber|jar|jms|keyparc|lastfm|ldap|ldaps|leaptofrogans|lorawan|lvlt|magnet|mailserver|mailto|maps|market|message|mid|mms|modem|mongodb|moz|ms-access|ms-browser-extension|ms-calculator|ms-drive-to|ms-enrollment|ms-excel|ms-eyecontrolspeech|ms-gamebarservices|ms-gamingoverlay|ms-getoffice|ms-help|ms-infopath|ms-inputapp|ms-lockscreencomponent-config|ms-media-stream-id|ms-mixedrealitycapture|ms-mobileplans|ms-officeapp|ms-people|ms-project|ms-powerpoint|ms-publisher|ms-restoretabcompanion|ms-screenclip|ms-screensketch|ms-search|ms-search-repair|ms-secondary-screen-controller|ms-secondary-screen-setup|ms-settings|ms-settings-airplanemode|ms-settings-bluetooth|ms-settings-camera|ms-settings-cellular|ms-settings-cloudstorage|ms-settings-connectabledevices|ms-settings-displays-topology|ms-settings-emailandaccounts|ms-settings-language|ms-settings-location|ms-settings-lock|ms-settings-nfctransactions|ms-settings-notifications|ms-settings-power|ms-settings-privacy|ms-settings-proximity|ms-settings-screenrotation|ms-settings-wifi|ms-settings-workplace|ms-spd|ms-sttoverlay|ms-transit-to|ms-useractivityset|ms-virtualtouchpad|ms-visio|ms-walk-to|ms-whiteboard|ms-whiteboard-cmd|ms-word|msnim|msrp|msrps|mss|mtqp|mumble|mupdate|mvn|news|nfs|ni|nih|nntp|notes|ocf|oid|onenote|onenote-cmd|opaquelocktoken|openpgp4fpr|pack|palm|paparazzi|payto|pkcs11|platform|pop|pres|prospero|proxy|pwid|psyc|pttp|qb|query|redis|rediss|reload|res|resource|rmi|rsync|rtmfp|rtmp|rtsp|rtsps|rtspu|s3|secondlife|service|session|sftp|sgn|shttp|sieve|simpleledger|sip|sips|skype|smb|sms|smtp|snews|snmp|soap\.beep|soap\.beeps|soldat|spiffe|spotify|ssh|steam|stun|stuns|submit|svn|tag|teamspeak|tel|teliaeid|telnet|tftp|tg|things|thismessage|tip|tn3270|tool|turn|turns|tv|udp|unreal|urn|ut2004|v-event|vemmi|ventrilo|videotex|vnc|view-source|wais|webcal|wpid|ws|wss|wtai|wyciwyg|xcon|xcon-userid|xfire|xmlrpc\.beep|xmlrpc\.beeps|xmpp|xri|ymsgr|z39\.50|z39\.50r|z39\.50s):\/\/(((?:[_\.\p{L}\p{N}-]|%[0-9A-Fa-f]{2})+:)?((?:[_\.\p{L}\p{N}-]|%[0-9A-Fa-f]{2})+)@)?(([\p{L}\p{N}\p{S}_\.-])+(\.?([\p{L}\p{N}]|xn--[\p{L}\p{N}-]+)+\.?)|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}|\[(?:(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){6})(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:::(?:(?:(?:[0-9a-f]{1,4})):){5})(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:[0-9a-f]{1,4})))?::(?:(?:(?:[0-9a-f]{1,4})):){4})(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,1}(?:(?:[0-9a-f]{1,4})))?::(?:(?:(?:[0-9a-f]{1,4})):){3})(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,2}(?:(?:[0-9a-f]{1,4})))?::(?:(?:(?:[0-9a-f]{1,4})):){2})(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,3}(?:(?:[0-9a-f]{1,4})))?::(?:(?:[0-9a-f]{1,4})):)(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,4}(?:(?:[0-9a-f]{1,4})))?::)(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,5}(?:(?:[0-9a-f]{1,4})))?::)(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,6}(?:(?:[0-9a-f]{1,4})))?::))))\])(:[0-9]+)?(?:\/(?:[\p{L}\p{N}._~!$&'()*+,;=:@-]|%[0-9A-Fa-f]{2})*)*(?:\?(?:[\p{L}\p{N}._~!$&'\[\]()*+,;=:@/?-]|%[0-9A-Fa-f]{2})*)?(?:#(?:[\p{L}\p{N}._~!$&'()*+,;=:@/?-]|%[0-9A-Fa-f]{2})*)?$/ui
    if (regex.exec(value) !== null) {
      return true
    } else {
      return false
    }
  },
  params: ['url'],
  message: (_, values) => i18n.t('validations.url', values)
})

/*
same

# The given field must match the field under validation.
*/
extend('same', {
  validate (value, args) {
    // eslint-disable-next-line
    return value == args.other
  },
  params: [{ name: 'other', isTarget: true }],
  message: (_, values) => i18n.t('validations.same', values)
})

// TODO: Do examples for customRules
const customRules = {
  required_unless: {
    description: 'The field under validation must be present and not empty unless the anotherfield field is equal to any value.'
  },
  required_with: {
    description: 'The field under validation must be present and not empty only if any of the other specified fields are present.'
  },
  required_with_all: {
    description: 'The field under validation must be present and not empty only if all of the other specified fields are present.'
  },
  required_without: {
    description: 'The field under validation must be present and not empty only when any of the other specified fields are not present.'
  },
  required_without_all: {
    description: 'The field under validation must be present and not empty only when all of the other specified fields are not present.'
  },
  sometimes: {},
  nullable: {},
  url: {
    description: 'The field under validation must be a valid URL.'
  },
  same: {
    description: 'The given field must match the field under validation.'
  }
}

const validationRules = DataUtils.mergeObjects(veeValidateRules, customRules)
export default validationRules
